import { useMemo, useState } from "react";
import { C, S } from "../customers/theme";
import { SUPPLIER_STATUSES, PAYMENT_TERMS, CURRENCIES } from "../lib/suppliers";

let _seq = 0;
function Field({ label, value, onChange, type = "text", options, placeholder, step, suffix, autoFocus }) {
  const id = useMemo(() => `sf-${++_seq}`, []);
  return (
    <div>
      <label htmlFor={id} style={S.label}>{label}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {options ? (
          <select id={id} style={S.input} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
            {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <input id={id} style={S.input} type={type} step={step} value={value ?? ""} placeholder={placeholder} autoFocus={autoFocus}
            onChange={(e) => onChange(e.target.value)} />
        )}
        {suffix && <span style={{ color: C.textMuted, fontSize: 13 }}>{suffix}</span>}
      </div>
    </div>
  );
}

const NUMERIC = ["lead_time_days", "credit_limit"];

/**
 * Add/Edit supplier modal. Delegates persistence to onSave (which calls the
 * suppliers data layer); never touches Supabase directly.
 */
export default function SupplierForm({ initial, onCancel, onSave }) {
  const seed = initial || {};
  const isEdit = !!seed.id;
  const [f, setF] = useState({
    company_name: seed.company_name || "",
    trading_name: seed.trading_name || "",
    vat_number: seed.vat_number || "",
    company_reg_number: seed.company_reg_number || "",
    website: seed.website || "",
    status: seed.status || "active",
    contact_name: seed.contact_name || "",
    email: seed.email || "",
    phone: seed.phone || "",
    mobile: seed.mobile || "",
    address_line1: seed.address_line1 || "",
    address_line2: seed.address_line2 || "",
    city: seed.city || "",
    county: seed.county || "",
    postal_code: seed.postal_code || "",
    country: seed.country || "Ireland",
    payment_terms: seed.payment_terms || "net_30",
    preferred_currency: seed.preferred_currency || "EUR",
    lead_time_days: seed.lead_time_days ?? "",
    credit_limit: seed.credit_limit ?? "",
    notes: seed.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setError("");
    if (!f.company_name.trim()) { setError("Company name is required."); return; }
    const payload = { ...f };
    NUMERIC.forEach((k) => { payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k]); });
    setBusy(true);
    try {
      await onSave(payload);
    } catch (e) {
      setError(e.message || "Could not save supplier.");
      setBusy(false);
    }
  };

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={S.modalBox}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {isEdit ? `Edit ${seed.company_name || "Supplier"}` : "New Supplier"}
            {seed.supplier_number && <span style={{ color: C.textMuted, fontWeight: 500, fontSize: 13, marginLeft: 8, fontFamily: "monospace" }}>{seed.supplier_number}</span>}
          </h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>

        <div style={S.sectionTitle}>Company</div>
        <div style={S.grid2}>
          <Field label="Company Name *" value={f.company_name} onChange={set("company_name")} placeholder="Atlantic Wholesale Ltd" autoFocus />
          <Field label="Trading Name" value={f.trading_name} onChange={set("trading_name")} />
          <Field label="VAT Number" value={f.vat_number} onChange={set("vat_number")} placeholder="IE1234567T" />
          <Field label="Company Reg. Number" value={f.company_reg_number} onChange={set("company_reg_number")} />
          <Field label="Website" value={f.website} onChange={set("website")} placeholder="https://" />
          <Field label="Status" value={f.status} onChange={set("status")} options={SUPPLIER_STATUSES} />
        </div>

        <div style={S.sectionTitle}>Contact</div>
        <div style={S.grid2}>
          <Field label="Contact Name" value={f.contact_name} onChange={set("contact_name")} />
          <Field label="Email" type="email" value={f.email} onChange={set("email")} />
          <Field label="Phone" value={f.phone} onChange={set("phone")} />
          <Field label="Mobile" value={f.mobile} onChange={set("mobile")} />
        </div>

        <div style={S.sectionTitle}>Address</div>
        <div style={S.grid2}>
          <Field label="Address Line 1" value={f.address_line1} onChange={set("address_line1")} />
          <Field label="Address Line 2" value={f.address_line2} onChange={set("address_line2")} />
          <Field label="Town / City" value={f.city} onChange={set("city")} />
          <Field label="County / State" value={f.county} onChange={set("county")} />
          <Field label="Eircode / Postal Code" value={f.postal_code} onChange={set("postal_code")} />
          <Field label="Country" value={f.country} onChange={set("country")} />
        </div>

        <div style={S.sectionTitle}>Commercial</div>
        <div style={S.grid2}>
          <Field label="Payment Terms" value={f.payment_terms} onChange={set("payment_terms")} options={PAYMENT_TERMS} />
          <Field label="Currency" value={f.preferred_currency} onChange={set("preferred_currency")} options={CURRENCIES} />
          <Field label="Lead Time" type="number" step="1" suffix="days" value={f.lead_time_days} onChange={set("lead_time_days")} />
          <Field label="Credit Limit" type="number" step="0.01" suffix="€" value={f.credit_limit} onChange={set("credit_limit")} />
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={S.label}>Notes</label>
          <textarea style={{ ...S.input, height: 60 }} value={f.notes} onChange={(e) => set("notes")(e.target.value)} />
        </div>

        {error && <div style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 14 }} role="alert">{error}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 22, justifyContent: "flex-end" }}>
          <button type="button" style={S.btn("secondary")} onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" style={{ ...S.btn(), opacity: busy ? 0.6 : 1 }} onClick={submit} disabled={busy}>
            {busy ? "Saving…" : isEdit ? "Save Changes" : "Create Supplier"}
          </button>
        </div>
      </div>
    </div>
  );
}
